import { useSelector } from '@tunasong/redux'
import { Button, Typography, useLocation, useNavigate } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useRef } from 'react'
import { Browser } from 'react-kawaii'
import styles from './entity-access-denied.module.css'

/**
 * Shown when an entity cannot be loaded because the user may not see it (GH #1731).
 *
 * No "Try again": a permission denial does not change on retry. No "Request access" either — there
 * is no request-access flow to send the user to. The owner is not named because on this path it is
 * not known: the entity read is what was denied.
 */
export const EntityAccessDenied: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const email = useSelector(state => state.user.email)
  const headingRef = useRef<HTMLElement>(null)

  /** Move focus to the heading so screen readers announce the state, not the page they left. */
  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  /**
   * Only offer "Go back" for an in-app previous entry. `history.length` also counts other sites and the
   * sign-in redirect, so a shared link opened from chat or email would send the user out of the app.
   * React Router keys the first entry of a session 'default'.
   */
  const canGoBack = location.key !== 'default'

  return (
    <section className={styles.container} aria-labelledby="entity-access-denied-heading">
      <div aria-hidden="true">
        <Browser size={160} mood="sad" color={'var(--tuna-palette-secondary-main)'} />
      </div>
      <Typography id="entity-access-denied-heading" variant="h5" ref={headingRef} tabIndex={-1} className={styles.heading}>
        You don&apos;t have access to this
      </Typography>
      <Typography variant="body1" color="textSecondary" className={styles.body}>
        {email
          ? `You're signed in as ${email}. If someone shared it with you, ask them to share it with this address.`
          : 'If someone shared it with you, ask them to give you access.'}
      </Typography>
      <div className={styles.actions}>
        {canGoBack ? (
          <Button onClick={() => void navigate(-1)} variant="contained">
            Go back
          </Button>
        ) : null}
        <Button onClick={() => void navigate('/app')} variant={canGoBack ? 'text' : 'contained'}>
          Go home
        </Button>
      </div>
    </section>
  )
}
